import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFinca } from '../context/FincaContext';
import { usePermisos } from '../context/PermisosContext';
import { useAlertasPush } from '../hooks/useAlertasPush';
import AlertaDietaOverlay from '../components/AlertaDietaOverlay';
import AvisoLicencia from '../components/AvisoLicencia';

const grupos = [
  {
    titulo: null,
    items: [
      { to: '/',                 label: 'Dashboard',           icon: '📊', permiso: 'dashboard' },
      { to: '/monitoreo',        label: 'Monitoreo',           icon: '🔍', permiso: 'monitoreo.ver' },
      { to: '/registro-biologo', label: 'Registro Biólogo',    icon: '🔬', permiso: 'operacion.registro-biologo' },
      { to: '/mapa',             label: 'Mapas',               icon: '🗺️', permiso: 'operacion.piscinas' },
      { to: '/muestreos',        label: 'Muestreos',           icon: '📋', permiso: 'operacion.muestreos' },
      { to: '/parametros',       label: 'Parámetros',          icon: '🗒️', permiso: 'operacion.parametros' },
      { to: '/cosechas',         label: 'Raleos / Cosechas',   icon: '🦐', permiso: 'operacion.cosechas' },
      { to: '/reportes',         label: 'Reportes y Métricas', icon: '📈', permiso: 'reportes' },
    ],
  },
  {
    titulo: 'Alimentación',
    items: [
      { to: '/alimentacion/actividades', label: 'Actividades', icon: '🚨', permiso: 'alimentacion.actividades' },
      { to: '/alimentacion/reportes',    label: 'Reportes',    icon: '📄', permiso: 'alimentacion.reportes' },
    ],
  },
  {
    titulo: 'Facturación',
    items: [
      { to: '/pagar',          label: 'Renovar plan',   icon: '💳', permiso: 'configuracion' },
      { to: '/planes',         label: 'Planes',         icon: '📋', soloSuperAdmin: true },
      { to: '/suscripciones',  label: 'Suscripciones',  icon: '🗓️', soloSuperAdmin: true },
      { to: '/pagos',          label: 'Pagos',          icon: '💰', soloSuperAdmin: true },
      { to: '/config-pagos',   label: 'Config. pagos',  icon: '⚙️', soloSuperAdmin: true },
    ],
  },
  {
    titulo: 'Sistemas',
    items: [
      { to: '/demos',         label: 'Cuentas Demo',  icon: '🎬', soloSuperAdmin: true },
      { to: '/configuracion', label: 'Configuración', icon: '⚙️', permiso: 'configuracion' },
      { to: '/ayuda',         label: 'Ayuda',         icon: '❓', siempre: true },
      { to: '/descargar-app', label: 'Descargar App', icon: '📱', siempre: true },
    ],
  },
];

export default function MainLayout() {
  const { usuario, logout } = useAuth();
  const { fincas, fincaActiva, cambiarFinca, esSuperAdmin } = useFinca();
  const { tienePermiso } = usePermisos();
  const navigate = useNavigate();
  const location = useLocation();

  useAlertasPush();

  const gruposFiltrados = grupos
    .map((g) => ({
      ...g,
      items: g.items.filter((m) => {
        if (m.soloSuperAdmin) return esSuperAdmin;
        if (m.siempre) return true;
        return tienePermiso(m.permiso);
      }),
    }))
    .filter((g) => g.items.length > 0);

  const [menuAbierto, setMenuAbierto] = useState(false);
  const [colapsado, setColapsado] = useState(() => {
    const stored = localStorage.getItem('menuColapsado');
    return stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem('menuColapsado', colapsado);
    document.body.classList.toggle('sidebar-colapsado', colapsado);
  }, [colapsado]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const cerrarMenu = () => setMenuAbierto(false);

  // --- Rutas activas ---
  const esRutaMapa = location.pathname === '/mapa' || location.pathname.startsWith('/mapa/');
  const esRutaMonitoreo = location.pathname === '/monitoreo' || location.pathname.startsWith('/monitoreo/');
  const esRutaMuestreos = location.pathname === '/muestreos' || location.pathname.startsWith('/muestreos/');
  const esRutaParametros = location.pathname === '/parametros' || location.pathname.startsWith('/parametros/');
  const esRutaReportes = location.pathname === '/reportes' || location.pathname.startsWith('/reportes/');
  const esRutaCosechas = location.pathname === '/cosechas' || location.pathname.startsWith('/cosechas/');

  // --- Pestañas de Reportes ---
  const tabsReportes = [
    { id: 'registro-diario', label: 'Balanceado Consumido', icon: '📊', permiso: 'reportes.registro-diario' },
  ].filter(t => tienePermiso(t.permiso));

  // --- Pestañas de Cosechas ---
  const tabsCosechas = [
    { id: 'gestion', label: 'Gestión de Cosechas', icon: '🦐', permiso: 'operacion.cosechas' },
    { id: 'reporte', label: 'Reporte Raleo / Cosechas', icon: '📊', permiso: 'reportes.cosecha' },
  ].filter(t => tienePermiso(t.permiso));

  return (
    <AvisoLicencia>
      <div className="flex h-screen bg-gray-100">
        {menuAbierto && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
            onClick={cerrarMenu}
          />
        )}

        <aside
          className={`
            fixed inset-y-0 left-0 z-40
            bg-slate-600 text-white flex flex-col
            transform transition-all duration-300 ease-in-out
            shadow-2xl
            ${menuAbierto ? 'translate-x-0' : '-translate-x-full'}
            md:translate-x-0
            ${colapsado ? 'md:w-20' : 'md:w-64'}
            w-64
          `}
        >
          <div className={`border-b border-slate-500 flex items-center relative ${colapsado ? 'md:justify-center' : 'justify-center'} p-4`}>
            <div className="text-center">
              <div className="text-4xl">🦐</div>
              {!colapsado && (
                <div className="hidden md:block mt-2">
                  <div className="font-bold text-sm leading-tight">Gestión Alimentación</div>
                  <div className="font-bold text-sm leading-tight">Automática</div>
                </div>
              )}
            </div>

            <button
              onClick={cerrarMenu}
              className="md:hidden absolute top-3 right-3 text-white text-xl px-2"
              aria-label="Cerrar menú"
            >
              ✕
            </button>

            <button
              onClick={() => setColapsado(!colapsado)}
              className={`hidden md:flex absolute text-white/80 hover:text-white hover:bg-white/20 rounded-full w-8 h-8 items-center justify-center transition text-sm ${colapsado ? 'top-3 right-1' : 'top-3 right-2'}`}
              title={colapsado ? 'Expandir menú' : 'Colapsar menú'}
            >
              {colapsado ? '☰' : '✕'}
            </button>
          </div>

          <nav className="flex-1 p-2 overflow-y-auto">
            {gruposFiltrados.map((grupo, gi) => (
              <div key={grupo.titulo || `grupo-${gi}`} className={gi > 0 ? 'mt-4' : ''}>
                {grupo.titulo && !colapsado && (
                  <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-300 font-semibold">
                    {grupo.titulo}
                  </div>
                )}
                {colapsado && gi > 0 && (
                  <div className="my-2 border-t border-slate-500/60" />
                )}

                {grupo.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    onClick={cerrarMenu}
                    className={({ isActive }) =>
                      `group relative flex items-center rounded mb-1 transition
                       ${colapsado ? 'md:justify-center px-2 py-3' : 'gap-3 px-3 py-3'}
                        ${isActive
                         ? 'bg-white text-slate-700 font-semibold shadow'
                         : 'hover:bg-slate-500'
                       }`
                    }
                  >
                    <span className="text-xl flex-shrink-0">{item.icon}</span>
                    <span className={`${colapsado ? 'md:hidden' : ''}`}>{item.label}</span>

                    {colapsado && (
                      <span
                        className="hidden md:group-hover:block absolute left-full ml-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50 shadow-lg pointer-events-none"
                      >
                        {item.label}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        </aside>

        <div
          className={`
            flex-1 flex flex-col overflow-hidden
            transition-all duration-300 ease-in-out
            ${colapsado ? 'md:ml-20' : 'md:ml-64'}
          `}
        >
          <header className="bg-white border-b shadow-sm px-4 py-2 flex flex-col md:flex-row items-center justify-between gap-4 relative z-50 shrink-0">
            
            <div className="flex items-center gap-4 w-full md:w-auto">
              <button
                onClick={() => setMenuAbierto(true)}
                className="md:hidden text-primary text-2xl px-1"
                aria-label="Abrir menú"
              >
                ☰
              </button>

              <div className="md:hidden font-semibold text-primary">
                Gestión Alimentación Automática
              </div>

              {esRutaMapa && (
                <div className="hidden md:flex items-center gap-1">
                  <button
                    onClick={() => navigate('/mapa')}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition border-b-2 ${
                      !location.search.includes('tab=telecom')
                        ? 'text-primary border-primary bg-blue-50 font-bold'
                        : 'text-gray-500 border-transparent hover:text-primary hover:bg-gray-50'
                    }`}
                  >
                    🗺️ Mapeo Equipos Piscina
                  </button>
                  <button
                    onClick={() => navigate('/mapa?tab=telecom')}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition border-b-2 ${
                      location.search.includes('tab=telecom')
                        ? 'text-primary border-primary bg-blue-50 font-bold'
                        : 'text-gray-500 border-transparent hover:text-primary hover:bg-gray-50'
                    }`}
                  >
                    📡 Mapeo Telecomunicaciones
                  </button>
                </div>
              )}

              {esRutaMonitoreo && (
                <div className="hidden md:flex items-center gap-1">
                  <button
                    onClick={() => navigate('/monitoreo')}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition border-b-2 ${
                      !location.search.includes('tab=rb') && !location.search.includes('tab=am')
                        ? 'text-primary border-primary bg-blue-50 font-bold'
                        : 'text-gray-500 border-transparent hover:text-primary hover:bg-gray-50'
                    }`}
                  >
                    📊 Registro Diario
                  </button>
                  <button
                    onClick={() => navigate('/monitoreo?tab=rb')}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition border-b-2 ${
                      location.search.includes('tab=rb')
                        ? 'text-primary border-primary bg-blue-50 font-bold'
                        : 'text-gray-500 border-transparent hover:text-primary hover:bg-gray-50'
                    }`}
                  >
                    🔬 Registro Biólogo
                  </button>
                  <button
                    onClick={() => navigate('/monitoreo?tab=am')}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition border-b-2 ${
                      location.search.includes('tab=am')
                        ? 'text-primary border-primary bg-blue-50 font-bold'
                        : 'text-gray-500 border-transparent hover:text-primary hover:bg-gray-50'
                    }`}
                  >
                    🍤 Alimentación Manual
                  </button>
                </div>
              )}

              {esRutaMuestreos && (
                <div className="hidden md:flex items-center gap-1">
                  <button
                    onClick={() => navigate('/muestreos')}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition border-b-2 ${
                      !location.search.includes('tab=crecimientos')
                        ? 'text-primary border-primary bg-blue-50 font-bold'
                        : 'text-gray-500 border-transparent hover:text-primary hover:bg-gray-50'
                    }`}
                  >
                    📋 Peso / Prepeso
                  </button>
                  <button
                    onClick={() => navigate('/muestreos?tab=crecimientos')}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition border-b-2 ${
                      location.search.includes('tab=crecimientos')
                        ? 'text-primary border-primary bg-blue-50 font-bold'
                        : 'text-gray-500 border-transparent hover:text-primary hover:bg-gray-50'
                    }`}
                  >
                    📈 Crecimientos
                  </button>
                </div>
              )}

              {esRutaParametros && (
                <div className="hidden md:flex items-center gap-1">
                  <button
                    onClick={() => navigate('/parametros')}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition border-b-2 ${
                      !location.search.includes('tab=grafica') && !location.search.includes('tab=comparar')
                        ? 'text-primary border-primary bg-blue-50 font-bold'
                        : 'text-gray-500 border-transparent hover:text-primary hover:bg-gray-50'
                    }`}
                  >
                    📋 Historial
                  </button>
                  <button
                    onClick={() => navigate('/parametros?tab=grafica')}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition border-b-2 ${
                      location.search.includes('tab=grafica')
                        ? 'text-primary border-primary bg-blue-50 font-bold'
                        : 'text-gray-500 border-transparent hover:text-primary hover:bg-gray-50'
                    }`}
                  >
                    📈 Gráfica
                  </button>
                  <button
                    onClick={() => navigate('/parametros?tab=comparar')}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition border-b-2 ${
                      location.search.includes('tab=comparar')
                        ? 'text-primary border-primary bg-blue-50 font-bold'
                        : 'text-gray-500 border-transparent hover:text-primary hover:bg-gray-50'
                    }`}
                  >
                    ⚖️ Comparar
                  </button>
                </div>
              )}

              {/* ✅ PESTAÑAS DE REPORTES Y MÉTRICAS */}
              {esRutaReportes && tabsReportes.length > 0 && (
                <div className="hidden md:flex items-center gap-1">
                  {tabsReportes.map((tab, index) => {
                    const isActive = index === 0 
                      ? (!location.search.includes('tab=') || location.search.includes(`tab=${tab.id}`))
                      : location.search.includes(`tab=${tab.id}`);
                    
                    return (
                      <button
                        key={tab.id}
                        onClick={() => navigate(`/reportes?tab=${tab.id}`)}
                        className={`px-4 py-2 text-sm font-medium rounded-t-lg transition border-b-2 flex items-center gap-2 ${
                          isActive
                            ? 'text-primary border-primary bg-blue-50 font-bold'
                            : 'text-gray-500 border-transparent hover:text-primary hover:bg-gray-50'
                        }`}
                      >
                        <span>{tab.icon}</span>
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* ✅ PESTAÑAS DE COSECHAS */}
              {esRutaCosechas && tabsCosechas.length > 0 && (
                <div className="hidden md:flex items-center gap-1">
                  {tabsCosechas.map((tab) => {
                    const isActive = location.search.includes(`tab=${tab.id}`) || (!location.search.includes('tab=') && tab.id === 'gestion');
                    return (
                      <button
                        key={tab.id}
                        onClick={() => navigate(`/cosechas?tab=${tab.id}`)}
                        className={`px-4 py-2 text-sm font-medium rounded-t-lg transition border-b-2 flex items-center gap-2 ${
                          isActive
                            ? 'text-primary border-primary bg-blue-50 font-bold'
                            : 'text-gray-500 border-transparent hover:text-primary hover:bg-gray-50'
                        }`}
                      >
                        <span>{tab.icon}</span>
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

            </div>

            <div className="flex items-center gap-3 w-full md:w-auto justify-end">
              {esSuperAdmin && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-600 hidden sm:inline">
                    🏢 Finca:
                  </label>
                  <select
                    value={fincaActiva?.id || ''}
                    onChange={(e) => {
                      const id = e.target.value;
                      if (!id) {
                        cambiarFinca(null);
                      } else {
                        const f = fincas.find(x => x.id === id);
                        if (f) cambiarFinca(f);
                      }
                    }}
                    className="px-3 py-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm bg-white"
                  >
                    <option value="">Todas las fincas</option>
                    {fincas.map((f) => (
                      <option key={f.id} value={f.id}>{f.nombre}</option>
                    ))}
                  </select>
                </div>
              )}

              {!esSuperAdmin && fincaActiva && (
                <div className="text-sm text-gray-500 hidden sm:block">
                  🏢 <strong className="text-gray-700">{fincaActiva.nombre}</strong>
                </div>
              )}

              <div className="w-px h-8 bg-gray-200 hidden sm:block"></div>

              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-slate-600 text-white flex items-center justify-center text-sm font-bold">
                  {usuario?.nombre?.charAt(0).toUpperCase()}
                </div>
                <div className="hidden sm:block leading-tight">
                  <div className="font-semibold text-sm text-gray-800">
                    {usuario?.nombre}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {usuario?.rol}
                  </div>
                </div>
              </div>

              <button
                onClick={handleLogout}
                className="ml-2 px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition font-medium"
                title="Cerrar sesión"
              >
                ⎋ Cerrar sesión
              </button>
            </div>
          </header>

          <main className="flex-1 overflow-hidden relative">
            <Outlet />
          </main>
        </div>

        <AlertaDietaOverlay />
      </div>
    </AvisoLicencia>
  );
}